import React from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@roger/ui-components';
import { CalendarClock } from 'lucide-react';
import { AgencyRail } from './AgencyRail';
import { rowDisplayName, type AgencyDataFillRow } from './agencyDataFillRows';
import {
  formatMoment,
  isKinnserOff,
  KINNSER_OFF_HINT,
} from './agencyDataFillView';

/**
 * The agencies whose Kinnser data an operator can fill.
 *
 * The list itself is ``AgencyRail``, which Demo Sandboxes draws too. This
 * supplies the two things that are Kinnser's: whether anything keeps the
 * agency up to date, and when it was last filled.
 *
 * Every agency is here, including the ones Kinnser is off for. A row that
 * offers nothing is still listed, so somebody looking for an agency learns
 * that rather than finding nothing.
 */

export const AgencyDataFillRail: React.FC<{
  rows: readonly AgencyDataFillRow[];
  selectedId: string | null;
  onSelect: (agencyId: string) => void;
}> = ({ rows, selectedId, onSelect }) => (
  <AgencyRail
    rows={rows.map(row => ({
      ...row,
      displayName: rowDisplayName(row),
      muted: isKinnserOff(row),
      mutedNote: 'Kinnser is off',
    }))}
    selectedId={selectedId}
    onSelect={onSelect}
    /* The one thing about an agency worth knowing before you pick it: whether
       anything keeps it up to date. Not clickable, so its clicks select the
       row like the rest of it. */
    badge={row => (
      <Tooltip>
        <TooltipTrigger asChild>
          {/* The label rides on the span: lucide marks its svg aria-hidden. */}
          <span
            role='img'
            aria-label={`Daily sync for ${row.displayName}`}
            className='inline-flex shrink-0'
          >
            <CalendarClock
              className={`h-4 w-4 ${
                row.kinnserEnabled
                  ? 'text-green-700'
                  : 'text-muted-foreground/30'
              }`}
            />
          </span>
        </TooltipTrigger>
        <TooltipContent className='max-w-[260px]'>
          {row.kinnserEnabled
            ? 'This agency syncs from Kinnser automatically.'
            : KINNSER_OFF_HINT}
        </TooltipContent>
      </Tooltip>
    )}
    /**
     * The one line under the name: when this agency last got its Kinnser data.
     *
     * When it was last *read* is left out on purpose. Two dates in an 11px
     * line read as a range, and the fill is the one that answers "has this
     * agency got its Kinnser data" -- which is what somebody scanning the rail
     * is asking. Both dates, with who asked for them, are in the pane.
     */
    state={row =>
      row.muted
        ? 'Kinnser off'
        : row.appliedAt
          ? `Filled ${formatMoment(row.appliedAt)}`
          : 'Never filled'
    }
  />
);

export default AgencyDataFillRail;
